import { useEffect, useRef, useState } from "react";

import { logger } from "interfaces/logger";

import { isMediaRecorderFullySupported } from "./device";

const FFTSIZE = 2048; // https://developer.mozilla.org/en-US/docs/Web/API/AnalyserNode/fftSize
const BUFFER_LENGTH = FFTSIZE / 2; //  https://developer.mozilla.org/en-US/docs/Web/API/AnalyserNode/frequencyBinCount

// This hook accepts a stream and returns the volume in db every 100ms
export const useStreamVolume = (stream?: MediaStream) => {
  const [volumeInDb, setVolumeInDb] = useState<number>();
  const audioContextRef = useRef(
    new (window.AudioContext || window.webkitAudioContext)()
  );
  const analyserRef = useRef(audioContextRef.current.createAnalyser());
  const intervalRef = useRef<NodeJS.Timer>();

  useEffect(() => {
    // set FFT Size
    analyserRef.current.fftSize = FFTSIZE;
  }, []);

  useEffect(() => {
    // clear old interval with prev stream volume
    clearInterval(intervalRef.current);
    if (!stream) return;
    // Create a new audio source for the new stream and connect it to the analyser
    audioContextRef.current
      .createMediaStreamSource(stream)
      .connect(analyserRef.current);

    // query volume every interval
    intervalRef.current = setInterval(() => {
      // create an 8-bit unsigned data array
      const waveform = new Uint8Array(BUFFER_LENGTH);
      // copy the current waveform from the analyser into the newly created 8-bit unsigned data array
      analyserRef.current.getByteTimeDomainData(waveform);
      // calculate the volume in dB from the waveform (as an 8-bit unsigned data array)
      setVolumeInDb(calculateVolumeFromWaveform(waveform));
    }, 100);
    return () => clearInterval(intervalRef.current);
  }, [stream]);

  return volumeInDb;
};

// accepts a waveform in a Uint8Array format and returns the volume in dB
// This math was shamelessly copy / pasted. I don't understand all of it
// but am happy with the results it produces - Anne
export const calculateVolumeFromWaveform = (dataArray: Uint8Array) => {
  // Calculate an average of the audio amplitude
  let sum = 0;
  for (let i = 0; i < BUFFER_LENGTH; i++) {
    const x = dataArray[i] / 128.0 - 1; // Normalize the values
    sum += x * x; // Calculate power
  }
  const rms = Math.sqrt(sum / BUFFER_LENGTH);
  const volume = Math.max(rms, 0.01); // Avoid -Infinity with log

  return 20 * Math.log10(volume);
};

// abstracted for ease of testing
export const getAudioStreamFromMediaSinkId = (sinkId: string) =>
  navigator.mediaDevices.getUserMedia({
    audio: { deviceId: { exact: sinkId } },
  });

// abstracted for ease of testing
export const getVideoStreamFromMediaSinkId = (sinkId: string) =>
  navigator.mediaDevices.getUserMedia({
    video: { deviceId: { exact: sinkId } },
  });

// abstracted for ease of testing
export const getMediaDevices = async (isVideoEnabled: boolean) => {
  let permissionStatus: "loading" | "granted" | "denied" = "loading";
  try {
    await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: isVideoEnabled,
    });
    permissionStatus = "granted";
  } catch (error) {
    permissionStatus = "denied";
  }

  const devices = await navigator.mediaDevices.enumerateDevices();
  const audioInputs = devices.filter((device) => device.kind === "audioinput");
  const audioOutputs = devices.filter(
    (device) => device.kind === "audiooutput"
  );
  const videoInputs = isVideoEnabled
    ? devices.filter((device) => device.kind === "videoinput")
    : [];

  return { audioInputs, audioOutputs, videoInputs, permissionStatus };
};

export const useMediaRecorder = ({
  stream,
  onRecordedMediaReady,
}: {
  stream?: MediaStream;
  onRecordedMediaReady: (value: File) => void;
}): { mediaRecorder?: MediaRecorder | undefined; error?: string } => {
  const [error, setError] = useState<string>();

  const mimeType = "video/mp4";

  const mediaRecorder = useRef<MediaRecorder>();
  useEffect(() => {
    if (
      !isMediaRecorderFullySupported() ||
      !MediaRecorder.isTypeSupported(mimeType)
    ) {
      setError(
        "Please use a more recent version of Chrome (>49) or Safari (>14.1) in order to proceed"
      );

      return;
    }
    if (!stream) return;
    const chunks: Blob[] = [];
    let chunksSize = 0;
    mediaRecorder.current = new MediaRecorder(stream, {
      mimeType,
      audioBitsPerSecond: 128000,
      videoBitsPerSecond: 512000,
    });
    // Event listener for when data is available
    mediaRecorder.current.ondataavailable = (event) => {
      chunks.push(event.data);
      chunksSize += event.data.size;
    };

    // Event listener for when we stop the recording
    mediaRecorder.current.onstop = () => {
      const videoBlob = new Blob(chunks, { type: mimeType });
      const newFile = new File([videoBlob], "video.mp4", { type: mimeType });
      if (newFile.size == 0 && chunksSize > 0) {
        logger(
          `File size [0] does not match total chunks sizes [${chunksSize}]`,
          "error"
        );
      }
      onRecordedMediaReady(newFile);
    };
  }, [stream, mediaRecorder, onRecordedMediaReady]);

  return { mediaRecorder: mediaRecorder.current, error };
};
